import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./ServiceDetail.css"; // Reusing the same CSS

const servicesData = [
  {
    id: 1,
    title: "Workday Advisory",
    icon: "🎯",
    description: "Strategic guidance and roadmap planning for successful Workday implementation.",
    color: "linear-gradient(135deg, #0073ff 0%, #2980b9 100%)",
    category: "Workday Advisory",
    expertiseLevel: "Advanced",
    deliveryTime: "4-8 weeks"
  },
  {
    id: 2,
    title: "Workday AMS",
    icon: "🛡️",
    description: "24/7 Application Management Services for continuous optimization.",
    color: "linear-gradient(135deg, #3498db 0%, #2c3e50 100%)",
    category: "Workday AMS",
    expertiseLevel: "All Levels",
    deliveryTime: "Ongoing"
  },
  {
    id: 3,
    title: "Workday Experts on Demand",
    icon: "👥",
    description: "Flexible access to certified Workday experts for your specific needs.",
    color: "linear-gradient(135deg, #1abc9c 0%, #16a085 100%)",
    category: "Workday Experts on Demand",
    expertiseLevel: "Expert",
    deliveryTime: "Flexible"
  },
  {
    id: 4,
    title: "Workday Test Automation",
    icon: "🤖",
    description: "Automated testing solutions ensuring quality and speed.",
    color: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)",
    category: "Workday Test Automation",
    expertiseLevel: "Intermediate",
    deliveryTime: "4-12 weeks"
  },
  {
    id: 5,
    title: "Workday Certifications",
    icon: "🏆",
    description: "Official Workday certification programs for your team.",
    color: "linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)",
    category: "Workday",
    expertiseLevel: "Beginner to Advanced",
    deliveryTime: "2-8 weeks"
  },
  {
    id: 6,
    title: "Workday Training",
    icon: "📚",
    description: "Comprehensive training programs for all user levels.",
    color: "linear-gradient(135deg, #f39c12 0%, #d35400 100%)",
    category: "Workday Training",
    expertiseLevel: "All Levels",
    deliveryTime: "2-6 weeks"
  }
];

const AllServices = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = [...new Set(servicesData.map(service => service.category))];

  return (
    <div className="all-services-page">
      {/* Header Section */}
      <section className="all-services-header">
        <div className="all-services-header-container">
          <button onClick={() => navigate("/services")} className="back-button">
            ← Back to Main Services
          </button>
          
          <div className="all-services-header-content">
            <h1>All Workday Services</h1>
            <p className="all-services-subtitle">
              Explore our comprehensive suite of Workday services designed to meet every aspect of your enterprise needs
            </p>
            
            <div className="category-filters">
              <button className="category-filter active">All Services</button>
              {categories.map(category => (
                <button key={category} className="category-filter">
                  {category}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="all-services-grid-section">
        <div className="all-services-grid-container">
          {servicesData.map((service) => (
            <div 
              key={service.id} 
              className="all-service-card"
              onClick={() => navigate(`/services/${service.id}`)}
            >
              <div className="all-service-card-header">
                <div className="all-service-icon" style={{ background: service.color }}>
                  {service.icon}
                </div>
                <div className="all-service-category">
                  <span className="category-badge">{service.category}</span>
                  <span className="expertise-badge">{service.expertiseLevel}</span>
                </div>
              </div>
              
              <div className="all-service-card-body">
                <h3>{service.title}</h3>
                <p className="all-service-description">{service.description}</p>
                
                <div className="all-service-meta">
                  <div className="meta-item">
                    <span className="meta-label">Delivery Time:</span>
                    <span className="meta-value">{service.deliveryTime}</span>
                  </div>
                  <div className="meta-item">
                    <span className="meta-label">Engagement:</span>
                    <span className="meta-value">Flexible Models</span>
                  </div>
                </div>
                
                <div className="all-service-features">
                  <h4>Key Highlights:</h4>
                  <ul>
                    <li>✓ Certified Workday Experts</li>
                    <li>✓ Proven Methodologies</li>
                    <li>✓ Global Delivery</li>
                    <li>✓ 24/7 Support</li>
                  </ul>
                </div>
                
                <button className="btn-explore-service">
                  Explore Service Details →
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="services-comparison-section">
        <div className="services-comparison-container">
          <h2>Service Comparison</h2>
          <p className="comparison-subtitle">
            Compare our services to find the perfect fit for your organization
          </p>
          
          <div className="comparison-table-responsive">
            <table className="services-comparison-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Best For</th>
                  <th>Delivery Model</th>
                  <th>Typical Duration</th>
                  <th>Expertise Level</th>
                </tr>
              </thead>
              <tbody>
                {servicesData.map(service => (
                  <tr key={service.id}>
                    <td>
                      <div className="service-row" onClick={() => navigate(`/services/${service.id}`)}>
                        <span className="service-icon-small">{service.icon}</span>
                        {service.title}
                      </div>
                    </td>
                    <td>
                      {service.id === 1 && "Strategic planning & implementation"}
                      {service.id === 2 && "Ongoing support & maintenance"}
                      {service.id === 3 && "Flexible project staffing"}
                      {service.id === 4 && "Quality assurance & testing"}
                      {service.id === 5 && "Team certification & validation"}
                      {service.id === 6 && "User training & adoption"}
                    </td>
                    <td>
                      {service.id === 2 ? "Managed Service" : 
                       service.id === 5 || service.id === 6 ? "Fixed Price" : 
                       "Time & Materials"}
                    </td>
                    <td>{service.deliveryTime}</td>
                    <td>{service.expertiseLevel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="service-categories-section">
        <div className="service-categories-container">
          <h2>Service Categories</h2>
          <div className="categories-grid">
            {categories.map(category => {
              const categoryServices = servicesData.filter(s => s.category === category);
              // Get icon based on category name
              const categoryIcon = category === "Workday Advisory" ? "🎯" :
                                 category === "Workday AMS" ? "🛡️" :
                                 category === "Workday Experts on Demand" ? "👥" :
                                 category === "Workday Test Automation" ? "🤖" :
                                 category === "Workday" ? "🏆" : // For "Workday" category (Certifications)
                                 "📚"; // Default for Workday Training
              
              return (
                <div key={category} className="category-card">
                  <div className="category-icon">{categoryIcon}</div>
                  <h3>{category}</h3>
                  <p className="category-count">{categoryServices.length} Services</p>
                  <div className="category-services">
                    {categoryServices.map(service => (
                      <div 
                        key={service.id} 
                        className="category-service-item"
                        onClick={() => navigate(`/services/${service.id}`)}
                      >
                        {service.title}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="all-services-cta">
        <div className="all-services-cta-container">
          <div className="cta-content">
            <h2 style={{ color: "#0d6efd" }}>
              Need Help Choosing the Right Service?
            </h2>
            <p style={{ color: "#0d6efd" }}>
              Our Workday experts can help you select the perfect service combination 
              for your organization's unique needs and objectives.
            </p>
            
            <div className="cta-buttons">
              <button 
                className="btn-cta-primary" 
                onClick={() => navigate("/contact")}
              >
                Get Free Consultation
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AllServices;